import { Injectable } from "@angular/core";
import { AccountAppHandlerService } from "../handlers/account-app-handler.service";
import { AccountAppDeviceHandlerService } from "../handlers/account-app-device-handler.service";
import { AccountAppAudiencesHandlerService } from "../handlers/account-app-audiences-handler.service";
import { AccountAppUserHandlerService } from "../handlers/account-app-user-handler.service";
import { AccountAppDTO } from "./AccountApp";
import { AccountSubscriptionsHandlerService } from "../handlers/account-subscriptions-handler.service";
import { AccountAppUsersRegionCompareGroupHandlerService } from "../handlers/account-app-users-region-compare-group-handler.service";

export interface RegionCompareGroupDTO {
  id?: number;
  account_app_id: number;
  user_id: number;
  name: string;
  geofences_id: number[];
  beacon_regions_id: number[];
}

export interface RegionDTO {
  id: number;
  name: string;
  type: string;
  avg_tos: string;
  total_visits: number;
  unique_visits: number;
  avg_visit_freq: number;
  swapPanelOpen: boolean;
  typeName: string;
  createNewPallateSelected?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class AppService {
  constructor(
    private AccountAppHandlerService: AccountAppHandlerService,
    private AccountAppDeviceHandlerService: AccountAppDeviceHandlerService,
    private AccountAppAudiencesHandlerService: AccountAppAudiencesHandlerService,
    private AccountAppUserHandlerService: AccountAppUserHandlerService,
    private AccountSubscriptionsHandlerService: AccountSubscriptionsHandlerService,
    private AccountAppUsersRegionCompareGroupHandlerService: AccountAppUsersRegionCompareGroupHandlerService,
  ) {}

  getApps(
    masterAccountID: number,
    queryParams?: {
      facility_id?: number[];
      start?: string;
      end?: string;
      page?: number;
      perPage?: number;
    },
  ) {
    return this.AccountAppHandlerService.accountAppsGet(
      masterAccountID,
      queryParams,
    );
  }

  getApp(masterAccountID: number, accountAppID: number) {
    return this.AccountAppHandlerService.accountAppGet(
      masterAccountID,
      accountAppID,
    );
  }

  getFilterCount(masterAccountID: number, appID: number, filter: any) {
    return this.AccountAppDeviceHandlerService.filterCount(
      masterAccountID,
      appID,
      filter,
    );
  }

  getFilter(masterAccountID: number, appID: number, filter: any) {
    return this.AccountAppDeviceHandlerService.filter(
      masterAccountID,
      appID,
      filter,
    );
  }

  //LIST
  getAudiences(
    masterAccountID: number,
    accountAppID: number,
    queryParams?: { page?: number; perPage?: number },
  ) {
    return this.AccountAppAudiencesHandlerService.getAudiences(
      masterAccountID,
      accountAppID,
      queryParams,
    );
  }

  getAudience(
    masterAccountID: number,
    accountAppID: number,
    audienceID: number,
  ) {
    return this.AccountAppAudiencesHandlerService.getAudience(
      masterAccountID,
      accountAppID,
      audienceID,
    );
  }

  updateAudience(
    masterAccountID: number,
    accountAppID: number,
    audienceID: number,
    audience: any,
  ) {
    return this.AccountAppAudiencesHandlerService.putAudience(
      masterAccountID,
      accountAppID,
      audienceID,
      audience,
    );
  }

  createAudience(masterAccountID: number, accountAppID: number, audience: any) {
    return this.AccountAppAudiencesHandlerService.postAudience(
      masterAccountID,
      accountAppID,
      audience,
    );
  }

  deleteAudience(
    masterAccountID: number,
    accountAppID: number,
    audienceID: number,
  ) {
    return this.AccountAppAudiencesHandlerService.deleteAudience(
      masterAccountID,
      accountAppID,
      audienceID,
    );
  }

  getUser(masterAccountID: number, accountAppUserID: number) {
    return this.AccountAppUserHandlerService.getUser(
      masterAccountID,
      accountAppUserID,
    );
  }

  getAppUserLocationHistory(masterAccountID: number, accountAppUserID: number) {
    return this.AccountAppUserHandlerService.getLocationHistory(
      masterAccountID,
      accountAppUserID,
    );
  }

  getAppUserSignalHistory(masterAccountID: number, accountAppUserID: number) {
    return this.AccountAppUserHandlerService.getSignalHistory(
      masterAccountID,
      accountAppUserID,
    );
  }

  getAppDevice(masterAccountID: number, accountAppDeviceID: String) {
    return this.AccountAppDeviceHandlerService.getAccountAppDevice(
      masterAccountID,
      accountAppDeviceID,
    );
  }

  getAppDeviceLocationHistory(masterAccountID: number, accountAppDeviceID: String) {
    return this.AccountAppDeviceHandlerService.getAccountAppDeviceLocationHistory(
      masterAccountID,
      accountAppDeviceID,
    );

  }

  getAppDeviceSignalHistory(masterAccountID: number, accountAppDeviceID: String) {
    return this.AccountAppDeviceHandlerService.getAccountAppDeviceSignalHistory(
      masterAccountID,
      accountAppDeviceID,
    );
  }

  getExecutiveSummary(
    masterAccountID: number,
    facilities?: number[],
    start?: Date,
    end?: Date,
  ) {
    return this.AccountAppDeviceHandlerService.getExecutiveSummary(
      masterAccountID,
      facilities,
      start,
      end,
    );
  }

  createApp(masterAccountID: number, accountAppDTO: AccountAppDTO) {
    return this.AccountAppHandlerService.accountAppPost(
      masterAccountID,
      accountAppDTO,
    );
  }

  updateApp(
    masterAccountID: number,
    appID: number,
    accountAppDTO: AccountAppDTO,
  ) {
    return this.AccountAppHandlerService.accountAppPut(
      masterAccountID,
      appID,
      accountAppDTO,
    );
  }

  deleteApp(masterAccountID: number, appID: number) {
    return this.AccountAppHandlerService.accountAppDelete(
      masterAccountID,
      appID,
    );
  }

  getSubscription(masterAccountID: number, accountAppID: number) {
    return this.AccountSubscriptionsHandlerService.accountSubscriptionsGet(
      masterAccountID,
      accountAppID,
    );
  }

  getRegions(masterAccountID: number, start: Date, end: Date, forExport: boolean) {
    return this.AccountAppDeviceHandlerService.getRegions(
      masterAccountID,
      start,
      end,
      forExport
    );
  }

  createRegionCompareGroup(compareGroupDTO: RegionCompareGroupDTO) {
    return this.AccountAppUsersRegionCompareGroupHandlerService.postCompareGroup(
      compareGroupDTO,
    );
  }

  updateRegionCompareGroup(compareGroupDTO: RegionCompareGroupDTO) {
    return this.AccountAppUsersRegionCompareGroupHandlerService.putCompareGroup(
      compareGroupDTO,
    );
  }

  deleteRegionCompareGroup(compareGroupID: number) {
    return this.AccountAppUsersRegionCompareGroupHandlerService.deleteCompareGroup(
      compareGroupID,
    );
  }

  getRegionCompareGroups(userID: number, accountAppID: number) {
    return this.AccountAppUsersRegionCompareGroupHandlerService.postCompareGroupList(
      userID,
      accountAppID,
    );
  }

  getDeviceInfo(
    masterAccountID: number,
    accountAppID: number,
    system: string,
    start: Date,
    end: Date,
  ) {
    return this.AccountAppDeviceHandlerService.getDeviceInfo(
      masterAccountID,
      accountAppID,
      system,
      start,
      end,
    );
  }

  getInsights(
    masterAccountID: number,
    accountAppID: number,
    start: Date,
    end: Date,
  ) {
    return this.AccountAppDeviceHandlerService.getInsights(
      masterAccountID,
      accountAppID,
      start,
      end,
    );
  }

  getInsightRedemptions(
    masterAccountID: number,
    accountAppID: number,
    signalID: number,
    signalType: string,
    start: Date,
    end: Date,
  ) {
    return this.AccountAppDeviceHandlerService.getInsightRedemptions(
      masterAccountID,
      accountAppID,
      signalID,
      signalType,
      start,
      end,
    );
  }

  getCustomerInsights(
    masterAccountID: number,
    facilities: number[],
    start: Date,
    end: Date,
  ) {
    return this.AccountAppDeviceHandlerService.getCustomerInsights(
      masterAccountID,
      facilities,
      start,
      end,
    );
  }

  getStats(
    masterAccountID: number,
    accountAppID: number,
    start: Date,
    end: Date,
    group: string,
    accountApps: any,
    forExport: boolean
  ) {
    let stats = this.AccountAppHandlerService.getStats(
      masterAccountID,
      accountAppID,
      {
        start: start.toISOString(),
        end: end.toISOString(),
        group: group,
        account_apps: accountApps,
        forExport: forExport
      }
    );
    return stats;
  }

}
